import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { getWsUrl, API_BASE } from '@/utils/api';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const listenersRef = useRef(new Map());

  const connect = useCallback(() => {
    const businessId = user?.business_id || user?.active_business_id;
    if (!businessId || !(token || localStorage.getItem('token'))) return;

    let ws = null;
    let pingInterval = null;
    let cancelled = false;

    const retry = () => {
      if (!cancelled) reconnectTimeoutRef.current = setTimeout(open, 3000);
    };

    const open = async () => {
      let ticket;
      try {
        ticket = (await axios.post(`${API_BASE}/ws-ticket`)).data.ticket;
      } catch (err) {
        if (![401, 403].includes(err.response?.status)) retry();
        return;
      }
      if (cancelled) return;
      ws = new WebSocket(`${getWsUrl()}/api/ws?ticket=${encodeURIComponent(ticket)}`);

      ws.onopen = () => {
        setConnected(true);
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        setLastMessage(data);
        listenersRef.current.forEach(({ type, callback }) => {
          if (type === '*' || type === data.type) callback(data);
        });
      };

      ws.onclose = (event) => {
        setConnected(false);
        clearInterval(pingInterval);
        if (event.code === 4401 || event.code === 1008) return;
        retry();
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);

      wsRef.current = ws;
    };

    open();

    return () => {
      cancelled = true;
      clearInterval(pingInterval);
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
    };
  }, [user?.business_id, user?.active_business_id, token]);

  useEffect(() => {
    const cleanup = connect();
    
    return () => {
      if (cleanup) cleanup();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  const subscribe = useCallback((type, callback) => {
    const id = `${type}_${Date.now()}_${Math.random()}`;
    listenersRef.current.set(id, { type, callback });
    
    return () => {
      listenersRef.current.delete(id);
    };
  }, []);

  const value = {
    connected,
    lastMessage,
    subscribe
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;

// Refresca con eventos en vivo; consulta periódica de respaldo (60 s si hay tiempo real)
export const useLiveRefresh = (fetchFn, fallbackMs) => {
  const { connected, subscribe } = useWebSocket();
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;
  useEffect(() => {
    const id = setInterval(() => fnRef.current(), connected ? 60000 : fallbackMs);
    return () => clearInterval(id);
  }, [connected, fallbackMs]);
  useEffect(() => subscribe('*', () => fnRef.current()), [subscribe]);
};
